import jwtDecode from 'jwt-decode';
import { Dispatch } from 'redux';

import { getRouteStackPath } from '@portal/config/routes';
import Errors, { ErrorsMessages } from '@portal/enum/errors';
import {
  AccountStatus,
  AccountTaxIdentifierType,
} from '@portal/models/enumerators';
import { PermissionsCardType } from '@portal/models/enumerators/cards';
import centralNavigationService from '~/services/navigation';

import { setHeader } from '@portal/repositories/securitySdkConfig';
import AccountApi from '~/repositories/account';
import AuthApi from '~/repositories/auth';
import PendingDocumentsApi from '~/repositories/pendingDocuments';
import UserApi from '~/repositories/user';
import * as MessageService from '~/services/message';
import * as StorageService from '~/services/storage';

import { OperatorStatus } from '@portal/enum/accountStatus';
import { TokenMethod } from '@portal/enum/device';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { TabKeys } from '@portal/pages/Auth/PasswordRecovery/PasswordRecovery';
import { IFormData } from '@portal/pages/UserManager/CreateUserManager/CreateUserManager';
import { setAnalyticsUserId } from '@portal/services/firebase';
import { getApprovedPaymentAccountsByType } from '@portal/utils/paymentAccount';
import { browserName, osName } from 'react-device-detect';
import { version } from '~/package.json';
import { AccountActions, UserImpersonationActions } from '.';
import {
  AUTH_CHECK_LOGGED,
  AVAILABLE_PERMISSIONS,
  CREATE_OPERATOR,
  CREATE_USER,
  DELETE_USER,
  GET_BY_ID_USER,
  MY_OPERATORS,
  MY_USERS,
  MY_USERS_COUNT,
  NEED_CREATE_IN_COREBANKING,
  SET_CARDS_ELIGIBILITY,
  SET_CREATED_IN_COREBANK,
  SET_ISPB,
  SET_ME,
  SET_OPERATOR_STATIC_VALUE,
  SET_PENDING_DOCUMENTS,
  SET_PENDING_DOCUMENTS_ERROR,
  SET_ROLES,
  SET_USER_CONTRACT_TERMS,
  UNSET_DELETE_USER,
  UPDATE_ACCOUNT,
  UPDATE_PERMISSIONS_USER,
  UPDATE_USER,
} from './actionTypes';
import { logout } from './auth';
import { decreaseLoading, increaseLoading, LoadingAction } from './loading';

interface FilterOperators {
  limit: number;
  offset?: number;
  search?: string;
  roleGroudId: string;
}

export const requestSms = async (method: TokenMethod) => {
  const session = StorageService.getItem('session-token');
  const tokens: models.AuthResponse | null = session
    ? JSON.parse(session)
    : null;
  if (tokens && tokens.accessToken) {
    const decodedToken: models.AuthToken = jwtDecode(tokens.accessToken);
    try {
      await AuthApi.requestSms(decodedToken.userId, method);
    } catch (error) {
      if (error instanceof Error) {
        if (
          error.message.toLowerCase() === ErrorsMessages.MAX_DEVICES_PER_USER
        ) {
          MessageService.error(error.message, { originalError: error });
          localStorage.clear();
          await centralNavigationService.back();
          return;
        }
        MessageService.error(error.message, { originalError: error });
      }
    }
  }
};

export interface SetPendingDocumentsAction {
  type: string;
  payload: models.PendingDocument[];
}

export interface SetUserAction {
  type: string;
  payload: models.User;
}

export const getMe = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const deviceId = StorageService.getDeviceId();
    if (deviceId) {
      setHeader(StorageService.StorageItems.DEVICE_ID, deviceId);
    }

    const getMeResponse = await UserApi.getMe();

    if (typeof getMeResponse === 'string') {
      throw new Error(Errors.MISSING_KEYS);
    }

    const {
      user,
      pendingDocuments,
      userContractTerms,
      roles,
      ispb,
      cardEligibility,
    } = getMeResponse;

    if (!user || !user.account) {
      throw new Error(Errors.MISSING_KEYS);
    }

    if (user.account.status === AccountStatus.BLOCKED) {
      throw new Error(Errors.BLOCKED_ACCOUNT);
    }

    setAnalyticsUserId(user.id);
    const publicKey = StorageService.getItem(`publicKey_${user.id}`);
    const privateKey = StorageService.getItem(`privateKey_${user.id}`);

    if (!publicKey || !privateKey) {
      throw new Error(Errors.MISSING_KEYS);
    }

    if (!user.mustChangePassword && !user.mustChangePin) {
      if (pendingDocuments && pendingDocuments.length > 0) {
        dispatch({
          type: SET_PENDING_DOCUMENTS,
          payload: pendingDocuments,
        });
      } else if (
        user.account.status === AccountStatus.APPROVED ||
        user.account.status === AccountStatus.CLOSURE_REQUESTED
      ) {
        dispatch({
          type: SET_PENDING_DOCUMENTS,
          payload: [],
        });

        const paymentAccount = getApprovedPaymentAccountsByType(
          user,
          PaymentAccountType.ACCOUNT
        );

        if (paymentAccount.length === 1 && paymentAccount[0].id) {
          dispatch(AccountActions.getUserConfig(paymentAccount[0].id));
        }
      }
    }

    if (userContractTerms && userContractTerms.length > 0) {
      dispatch({
        payload: userContractTerms,
        type: SET_USER_CONTRACT_TERMS,
      });
    }

    dispatch({
      type: SET_CARDS_ELIGIBILITY,
      payload: cardEligibility?.enabled,
    });

    dispatch({
      payload: user,
      type: SET_ME,
    });

    if (ispb != null) {
      dispatch({
        type: SET_ISPB,
        payload: ispb,
      });
    }

    dispatch({
      payload: roles,
      type: SET_ROLES,
    });

    dispatch({
      type: AUTH_CHECK_LOGGED,
    });
  } catch (error) {
    if (error instanceof Error) {
      if (
        error.message === Errors.MISSING_KEYS ||
        error.message === Errors.UNAUTHORIZED_DEVICE ||
        error.message === Errors.MALFORMED_UTF8_DATA
      ) {
        const path = window.location.pathname;
        if (path !== '/selecionar-canal' && path !== '/solicitar-sms') {
          centralNavigationService.navigate('/selecionar-canal');
        }
      } else {
        MessageService.error(error.message, { originalError: error });
      }
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export type GetMeResponse = {
  user: models.User;
  pendingDocuments: models.PendingDocument[];
  userCards: bank.Card[];
};

export const getMeFirstLoad = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const deviceId = StorageService.getDeviceId();
    if (deviceId) {
      setHeader(StorageService.StorageItems.DEVICE_ID, deviceId);
      setHeader('model', browserName);
      setHeader('manufacturer', 'web_browser');
      setHeader('platform', osName);
      setHeader('appVersion', version);
    }

    const getMeResponse = await UserApi.getMe();

    if (typeof getMeResponse === 'string') {
      throw new Error(Errors.MISSING_KEYS);
    }

    const {
      user,
      pendingDocuments,
      userContractTerms,
      roles,
      ispb,
      cardEligibility,
    } = getMeResponse;

    if (!user) {
      if (pendingDocuments && pendingDocuments.length > 0) {
        dispatch({
          type: SET_PENDING_DOCUMENTS,
          payload: pendingDocuments,
        });
        return;
      }
      throw new Error(Errors.MISSING_KEYS);
    }

    setAnalyticsUserId(user.id);

    const publicKey = StorageService.getItem(`publicKey_${user.id}`);
    const privateKey = StorageService.getItem(`privateKey_${user.id}`);
    if (!publicKey || !privateKey) {
      throw new Error(Errors.MISSING_KEYS);
    }

    if (!user.account) {
      throw new Error(Errors.MISSING_KEYS);
    }

    if (
      user.account.status === AccountStatus.REJECTED ||
      user.account.status === AccountStatus.ERROR_TO_CREATE ||
      user.account.status === AccountStatus.CANCELED
    ) {
      throw new Error(Errors.REJECTED_ACCOUNT);
    }

    if (user.account.status === AccountStatus.BLOCKED) {
      throw new Error(Errors.BLOCKED_ACCOUNT);
    }

    if (pendingDocuments && pendingDocuments.length > 0) {
      dispatch({
        type: SET_PENDING_DOCUMENTS,
        payload: pendingDocuments,
      });
    }

    if (userContractTerms && userContractTerms.length > 0) {
      dispatch({
        payload: userContractTerms,
        type: SET_USER_CONTRACT_TERMS,
      });
    }

    dispatch({
      type: SET_CARDS_ELIGIBILITY,
      payload: cardEligibility?.enabled,
    });

    dispatch({
      payload: user,
      type: SET_ME,
    });

    if (ispb != null) {
      dispatch({
        type: SET_ISPB,
        payload: ispb,
      });
    }

    dispatch({
      payload: roles,
      type: SET_ROLES,
    });

    if (
      user.account.status === AccountStatus.APPROVED ||
      user.account.status === AccountStatus.CLOSURE_REQUESTED
    ) {
      if (window.location.pathname === '/inicio') {
        await MessageService.success('PAGES.AUTH.LOGIN.MESSAGES.WELCOME');
      }

      if (
        user.account.type === AccountTaxIdentifierType.PERSON &&
        window.location.pathname === '/'
      ) {
        centralNavigationService.navigate(
          getRouteStackPath('HOME', 'HOME_PAGE')
        );
      }

      if (user.mustChangePassword) {
        window.location.pathname !== '/trocar-credenciais/senha' &&
          centralNavigationService.navigate('/trocar-credenciais/senha');
      } else if (user.mustChangePin) {
        window.location.pathname !== '/trocar-credenciais/pin' &&
          centralNavigationService.navigate('/trocar-credenciais/pin');
      } else {
        try {
          const impersonationResponse = await dispatch(
            UserImpersonationActions.getUserImpersonationList()
          );

          if (
            impersonationResponse?.payload?.received &&
            impersonationResponse.payload.received.length > 0
          ) {
            centralNavigationService.navigate(
              getRouteStackPath('PROFILE', 'ACCOUNT_SELECTION')
            );
            return;
          }
        } catch (error) {
          if (error instanceof Error) {
            MessageService.error(error.message, { originalError: error });
          }
        }

        dispatch({
          type: AUTH_CHECK_LOGGED,
        });
        dispatch({
          type: SET_PENDING_DOCUMENTS,
          payload: [],
        });
      }
    }
  } catch (error) {
    if (error instanceof Error) {
      if (
        error.message === Errors.UNAUTHORIZED_DEVICE ||
        error.message === Errors.MISSING_KEYS ||
        error.message === Errors.MALFORMED_UTF8_DATA
      ) {
        const path = window.location.pathname;
        if (path !== '/selecionar-canal' && path !== '/solicitar-sms') {
          centralNavigationService.navigate('/selecionar-canal');
        }
      } else if (error.message === Errors.REJECTED_ACCOUNT) {
        await MessageService.warn('PAGES.AUTH.LOGIN.MESSAGES.NOT_ALLOWED');

        if (window.location.pathname !== '/') {
          centralNavigationService.navigate('/');
          StorageService.removeItem('session-token');
          dispatch(logout());
        }
      } else {
        MessageService.error(error.message, { originalError: error });
      }
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const myUsers = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const payload = await AccountApi.myUsers();
    dispatch({ type: MY_USERS, payload });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const myUsersCount = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const payload = await AccountApi.myUsersCount();
    dispatch({ type: MY_USERS_COUNT, payload });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const getOperators = (props: FilterOperators) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const payload = await UserApi.myOperators(
      props.limit ?? 5,
      props.offset ?? 0,
      props.search ?? '',
      props.roleGroudId ?? ''
    );
    dispatch({ type: MY_OPERATORS, payload: payload });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const seeStatusUpdate = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const payload = await UserApi.updateLastLogin();

    dispatch({
      type: NEED_CREATE_IN_COREBANKING,
      payload: payload.needCreateInCoreBank,
    });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const getSmsInCoreBanking = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await UserApi.getSmsCorebank();
    MessageService.success('PAGES.USER_MANAGER.SENDED_SMS');
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const getSmsForVinculateCompany = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await UserApi.getForVinculateCompany();
    MessageService.success('PAGES.USER_MANAGER.SENDED_SMS');
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const sendSmsInCoreBanking = (token: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await UserApi.sendSmsCorebank(token);
    MessageService.success('PAGES.USER_MANAGER.SENDED_SMS');

    dispatch({
      type: SET_OPERATOR_STATIC_VALUE,
      payload: OperatorStatus.AWAITING_LINKED,
    });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const sendSmsForVinculateCompany =
  (token: string) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await UserApi.sendSmsForVinculateCompany(token);
      MessageService.success('PAGES.USER_MANAGER.SENDED_SMS');
      dispatch({
        type: SET_CREATED_IN_COREBANK,
      });
      dispatch({
        type: SET_OPERATOR_STATIC_VALUE,
        payload: OperatorStatus.LINKED,
      });
      dispatch({
        type: NEED_CREATE_IN_COREBANKING,
        payload: false,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const createOperators = (data: IFormData) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await UserApi.createOperators(data);
    MessageService.success('PAGES.USER_MANAGER.CREATED_OPERATOR');
    dispatch({ type: CREATE_OPERATOR });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const availablePermissions = () => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const permissionsAvailable = await UserApi.availablePermissions();

    const permissionsToSetType = [...permissionsAvailable];

    const permissionsTypeArray = Object.entries({
      [PermissionsCardType.TRANSFER]: ['favored', 'transaction', 'balance'],
      [PermissionsCardType.PAYABLE]: ['payable', 'authorized-direct-debit'],
      [PermissionsCardType.PIX]: ['pix'],
      [PermissionsCardType.PROFILE]: [
        'account',
        'manage-groups',
        'openfinance',
        'device',
      ],
      [PermissionsCardType.CARDS]: ['card'],
      [PermissionsCardType.RECEIVABLE]: ['receivable'],
      [PermissionsCardType.PAYSLIP]: ['payslip'],
      [PermissionsCardType.EMPLOYEES]: ['employees'],
      [PermissionsCardType.TED]: ['ted'],
      [PermissionsCardType.P2P]: ['tef'],
      [PermissionsCardType.BILLET]: ['billet'],
    });

    permissionsToSetType.forEach((role) => {
      for (const [type, keyWordsToMatch] of permissionsTypeArray) {
        const flowToReceiveType = keyWordsToMatch.some((keyWord) =>
          role.value.includes(keyWord)
        );

        if (flowToReceiveType) {
          role.type = Number(type);
          break;
        }
      }
    });

    dispatch({ type: AVAILABLE_PERMISSIONS, payload: permissionsToSetType });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const createUser = (body: models.CreateUser) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await UserApi.createUser(body);
    dispatch({ type: CREATE_USER, payload: true });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch({ type: CREATE_USER, payload: false });
    dispatch(decreaseLoading());
  }
};

export const updateUser =
  (id: string, body: models.UpdateUser) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await UserApi.updateUser(id, body);
      dispatch({ type: UPDATE_USER, payload: true });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch({ type: UPDATE_USER, payload: false });
      dispatch(decreaseLoading());
    }
  };

export const deleteUser = (userId: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await UserApi.deleteUser(userId);
    dispatch({ type: DELETE_USER, payload: true });
    dispatch(myUsers());
    dispatch({ type: UNSET_DELETE_USER });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch({ type: DELETE_USER, payload: false });
    dispatch(decreaseLoading());
  }
};

export const unsetDeleteUser = () => ({
  type: UNSET_DELETE_USER,
});

export const getById = (userId: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const payload = await UserApi.getById(userId);
    const roles = payload.permissions.map(
      (role: models.PermissionsList) => role.value
    );
    dispatch({
      type: GET_BY_ID_USER,
      payload: {
        roles,
        name: payload.name,
      },
    });
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};
export const updatePermissions =
  (userId: string, body: string[]) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await UserApi.updatePermissions(body, userId);
      dispatch({ type: UPDATE_PERMISSIONS_USER, payload: true });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch({ type: UPDATE_PERMISSIONS_USER, payload: false });
      dispatch(decreaseLoading());
    }
  };

export interface SetPendingDocumentsErrorAction {
  type: string;
  payload: Error | null;
}

export const getPendingDocuments =
  () =>
  async (
    dispatch: Dispatch<
      LoadingAction | SetPendingDocumentsAction | SetPendingDocumentsErrorAction
    >
  ) => {
    dispatch(increaseLoading());
    try {
      const payload = await PendingDocumentsApi.getPendingDocuments();

      dispatch({
        type: SET_PENDING_DOCUMENTS_ERROR,
        payload: null,
      });
      dispatch({
        type: SET_PENDING_DOCUMENTS,
        payload,
      });
    } catch (err) {
      if (err instanceof Error) {
        dispatch({
          type: SET_PENDING_DOCUMENTS,
          payload: null,
        });
        dispatch({
          type: SET_PENDING_DOCUMENTS_ERROR,
          payload: err,
        });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export type UpdateAccountAction = {
  type: string;
  payload: {
    key: string;
    value: unknown;
  };
};

export const updateAccount = (
  key: string,
  value: unknown
): UpdateAccountAction => ({
  type: UPDATE_ACCOUNT,
  payload: {
    key,
    value,
  },
});

export const acceptTerms = (terms) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    await UserApi.acceptTerms(terms);
    dispatch(getMe());
  } catch (err) {
    if (err instanceof Error) {
      MessageService.error(err.message, { originalError: err });
    }
  } finally {
    dispatch(decreaseLoading());
  }
};

export const checkToken =
  (
    {
      taxIdentifier,
      login,
      token,
    }: {
      taxIdentifier: string;
      login: string;
      token: string;
    },
    setCurrentTab: (tab: string) => void
  ) =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await UserApi.checkToken({ taxIdentifier, login, token });

      setCurrentTab(TabKeys.CREDENTIALS);
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
